import '../env.d.ts';
import { setConsentVersionBasedOnModel } from '@utiqLoader/helpers/setConsentVersionBasedOnModel';
import { createCookie } from '@shared/cookies/createCookie';
import { checkConsentVersionCookieValue } from '@shared/cookies/checkConsentVersionCookieValue';
import { encodeToBase64 } from '@shared/conversion/encodeToBase64';
import { getBaseDomain } from '@shared/domain/getBaseDomain';
import { Ctx, Snap } from './core';
import { IdConnectDataType } from '@idConnect/types';
import { isAnyIdAvailable } from '@shared/helpers/idGraph/isAnyIdAvailable';
import { consoleLogger } from '@shared/logging/consoleLogger';
import { TemplateData } from '@shared/ui/elements/types';
import { getValidatedBase64Cookie } from '@shared/cookies/getValidatedBase64Cookie';
import {
  UtiqConsentVersionEntryName,
  UtiqConsentVersionPreviousEntryName,
  UtiqStatusEntryName,
} from '@shared/constants/utiq';
import { getUtiqOpenRtbEids } from '@idConnect/helpers/getUtiqOpenRtbEids';
import { deconstructUri } from '@shared/helpers/deconstructUri';
import { getUrlWithTransactionId } from '@shared/transactionId/getUrlWithTransactionId';

/**
These actions are used to trigger behaviour when one is INSIDE the machine

Mostly, if you want something to happen, you should setup its pre-conditions, then rerun the machine.
But sometimes, there are very common outcomes to multiple effects (eg: set consent to false).

These very common outcomes can live here
*/

export function setValidatedBase64Cookie(
  key: string,
  val: string,
  days: number,
) {
  window.document.cookie = createCookie({
    name: key,
    value: encodeToBase64(val),
    days,
    domain: getBaseDomain(window.location.hostname),
  });
}

export function clearConsent(): void {
  setValidatedBase64Cookie('utiq_consent_status', 'false', 0);
}

export function denyConsent(snap: Snap, ctx: Ctx): void {
  localStorage.removeItem('utiqEligibility');
  if (snap.cookies.utiq_consent_status !== 'false') {
    setValidatedBase64Cookie('utiq_consent_status', 'false', 180);
    ctx.journal.onConsentUpdateFinished = snap.cookies.utiq_consent_status;
  }
}

export function acceptConsent(
  snap: Snap,
  ctx: Ctx,
  templateData: TemplateData,
): void {
  const consentVersionCookie = getValidatedBase64Cookie(
    document.cookie,
    UtiqConsentVersionEntryName,
  );
  const consentVersionPreviousCookie = getValidatedBase64Cookie(
    document.cookie,
    UtiqConsentVersionPreviousEntryName,
  );

  const consentVersion = checkConsentVersionCookieValue(
    consentVersionCookie,
    window.Utiq?.data?.popupContent?.version,
  );

  const consentVersionPrevious = checkConsentVersionCookieValue(
    consentVersionPreviousCookie,
    window.Utiq?.data?.popupContent?.versionPrevious,
  );

  const currentConsentStatusCookie = getValidatedBase64Cookie(
    document.cookie,
    UtiqStatusEntryName,
  );

  const consentVersionValue = setConsentVersionBasedOnModel(
    window.Utiq?.initialConsentVersionCookie,
    consentVersion ?? undefined,
    consentVersionPrevious ?? undefined,
    currentConsentStatusCookie,
  );

  const consentVersionPreviousValue = consentVersionPrevious;

  if (snap.cookies.utiq_consent_status !== 'true') {
    setValidatedBase64Cookie('utiq_consent_status', 'true', 180);
    ctx.journal.onConsentUpdateFinished = snap.cookies.utiq_consent_status;
  }

  if (templateData?.version)
    setValidatedBase64Cookie(
      'utiq_consent_version',
      String(consentVersionValue),
      390,
    );

  if (templateData?.versionPrevious)
    setValidatedBase64Cookie(
      'utiq_consent_version_previous',
      String(consentVersionPreviousValue),
      390,
    );
}

export type EligibilityTrigger = 'consent_granted' | 'load';
export function checkEligibility(
  trigger: EligibilityTrigger,
  snap: Snap,
  config: Ctx['config'],
  axios: Exclude<Ctx['axios']['instance'], null>,
) {
  const { protocol, domain, path } = deconstructUri(
    `${config.utiqHost}/op/idconnect`,
  );

  if (!protocol || !domain || !path) {
    throw new Error(
      `Data missing from Utiq host - protocol: ${protocol}, domain: ${domain}, path: ${path}`,
    );
  }

  const url = getUrlWithTransactionId(
    `${protocol}://${domain}/${path}/mno-precheck?type=${trigger}${
      !snap.localStorage.utiqEligibility ? '&initial=true' : ''
    }`,
  );

  axios({ method: 'GET', url });
}

export function idConnectCallbackSuccess(
  snap: Snap,
  idConnectData: IdConnectDataType,
) {
  consoleLogger.logDebug('idConnectCallbackSuccess enter');
  localStorage.setItem('utiqPass', JSON.stringify(idConnectData));
  localStorage.setItem(
    'utiq_openrtb_eids',
    JSON.stringify(getUtiqOpenRtbEids(idConnectData)),
  );

  localStorage.setItem(
    'utiq_openrtb_eids',
    JSON.stringify(getUtiqOpenRtbEids()),
  );

  /* Create timestamp to limit the next data call if any id is present in the idGraph */
  consoleLogger.logDebug(
    'isAnyIdAvailable(idConnectData?.connectId?.idGraph)',
    isAnyIdAvailable(idConnectData?.connectId?.idGraph ?? []),
  );
  if (isAnyIdAvailable(idConnectData?.connectId?.idGraph ?? [])) {
    const oncePerTimeInterval =
      window.Utiq?.data?.clientConfig?.CALLS_CONFIG?.conditions?.timeConditions
        ?.oncePerTimeInterval;

    const currentDatetime = snap.now.getTime();
    consoleLogger.logDebug(
      '!snap.localStorage.utiqDataCallTimestamp',
      !snap.localStorage.utiqDataCallTimestamp,
    );
    if (!snap.localStorage.utiqDataCallTimestamp) {
      localStorage.setItem('utiqDataCallTimestamp', String(currentDatetime));
      consoleLogger.logDebug(
        `Local Storage utiqDataCallTimestamp not found. Created new timestamp as ${currentDatetime}. Exact time of localStorage entry creation: ${Date.now()}`,
      );
    } else if (
      snap.localStorage.utiqDataCallTimestamp &&
      snap.localStorage.utiqDataCallTimestamp +
        parseInt(String(oncePerTimeInterval), 10) <
        currentDatetime
    ) {
      localStorage.setItem('utiqDataCallTimestamp', String(currentDatetime));
      consoleLogger.logDebug(
        `Time interval passed. Updated utiqDataCallTimestamp time to ${currentDatetime}. Exact time of localStorage entry creation: ${Date.now()}`,
      );
    } else {
      consoleLogger.logDebug(
        `Local Storage utiqDataCallTimestamp found in localStorage with value ${snap.localStorage.utiqDataCallTimestamp}`,
      );
    }
  }
  consoleLogger.logDebug('idConnectCallbackSuccess exit');
}
